import { useState, useRef, useEffect, useCallback } from 'react';

import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Paper,
  CircularProgress,
  Button,
  Tooltip,
  Badge,
  ListItemText,
  Autocomplete,
} from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import axiosInstance, { endpoints } from 'src/utils/axios';

// AI IMG
import AIImg from 'src/assets/images/ai-logo-icon.png';
import selfHealText from 'src/assets/images/self-heal-text.png';

// components
import { Iconify } from 'src/components/iconify';
import { toast } from 'src/components/snackbar';

import { formatTime } from './utils';
import { useChat } from './context/chatbot-provider';

export default function ChatBot({ onClose, user }) {
  const messagesEndRef = useRef(null);
  const viewDialog = useBoolean();

  const { messages, setMessages, isConnected, isTyping, sendMessage, formData, setFormData } =
    useChat();

  const [inputValue, setInputValue] = useState('');
  const [selectedAsset, setSelectedAsset] = useState();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(
    (text) => {
      if (!text.trim()) return;

      const newMsg = {
        id: Date.now().toString(),
        type: 'user',
        content: text,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, newMsg]);
      sendMessage(text);
      setInputValue('');
    },
    [sendMessage, setMessages]
  );

  const handleRaiseTicket = useCallback(
    (msgId, command) => {
      if (command === 'yes') {
        sendMessage('Raise a ticket code = TK90657');
        setMessages((prevMessages) =>
          prevMessages.map((msg) => (msg.id === msgId ? { ...msg, raiseTicket: false } : msg))
        );
      } else {
        setMessages((prevMessages) =>
          prevMessages.map((msg) => (msg.id === msgId ? { ...msg, raiseTicket: false } : msg))
        );
      }
    },
    [sendMessage, setMessages]
  );

  // asset click
  const handleAssetClick = useCallback(
    (selectedMsg, asset) => {
      const dbMac = normalizeMac(asset?.mac);
      axiosInstance
        .get(`${endpoints.chatbot.getSysDetails}?mac=${dbMac}&agentCode=${asset?.agentCode}`)
        .then((res) => {
          const prompt = {
            asset,
            logs: res.data.length
              ? res.data?.importantLogs
              : 'No critical logs found in the last 2 hours.',
            systemInfo: res?.data?.systemInfo || 'No system information available.',
          };

          sendMessage(JSON.stringify(prompt, null, 2));
        })
        .catch((err) => {
          console.error('Error fetching logs:', err.message);
          const fallback = {
            asset,
            logs: '⚠️ Failed to fetch logs.',
          };
          sendMessage(JSON.stringify(fallback, null, 2));
        });

      setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== selectedMsg?.id));
      setSelectedAsset(asset);
    },
    [sendMessage, setMessages]
  );

  // submit
  const handleSubmit = useCallback(
    (data, assetId, selectedMsg) => {
      const { ticketTitle, description, ticketCategory } = data;
      if (ticketTitle && description && ticketCategory && assetId) {
        axiosInstance
          .post(endpoints.approvalList.ticketCreate, {
            ticketTitle,
            ticketCategory,
            description,
            assetId,
          })
          .then((res) => {
            console.log('res', res);
            setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== selectedMsg?.id));
            setFormData({
              ticketTitle: '',
              ticketCategory: '',
              description: '',
            });
            setSelectedAsset(null);
            const newMsg = {
              id: Date.now().toString(),
              type: 'assistant',
              isExcel: false,
              raiseTicket: false,
              isAssetsList: false,
              isSubmitTicket: false,
              assetList: [],
              content: `Thanks, ticket created ✅ (ID: ${res?.data?.ticket?.ticketID || 'N/A'})`,
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, newMsg]);
          })
          .catch((err) => toast.error(err.message));
      } else {
        toast.error('No enough data tp proceed!');
      }
    },
    [setFormData, setMessages]
  );

  // ---------------------

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          background: 'linear-gradient(100deg, #260434, #41065A)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Badge
            overlap="circular"
            color={isConnected ? 'success' : 'error'}
            variant="dot"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Box component="img" src={AIImg} alt="AI" sx={{ width: 24 }} />
          </Badge>
          <Box component="img" src={selfHealText} alt="selfHealText" sx={{ width: 100 }} />
        </Box>

        <IconButton onClick={onClose} size="small" sx={{ color: 'inherit', bgcolor: '#4A2959' }}>
          <Iconify icon="material-symbols:close" />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, p: 2, overflowY: 'auto', bgcolor: '#f9fafb' }}>
        {messages?.map((msg) => (
          <Box
            key={msg.id}
            sx={{
              display: 'flex',
              justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',

              gap: 1,
              mb: 1.5,
            }}
          >
            {msg.type !== 'user' && (
              <Avatar
                sx={{
                  bgcolor: '#4629F21F',
                }}
              >
                <Box component="img" src={AIImg} alt="ai" sx={{ width: 24 }} />
              </Avatar>
            )}
            <Box sx={{ maxWidth: '75%' }}>
              <Box
                sx={{
                  bgcolor: msg.type === 'user' ? '#661F84' : '#F1F7FF',
                  color: msg.type === 'user' ? '#fff' : 'text.primary',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  boxShadow: 1,
                }}
              >
                {msg.isAssetsList ? (
                  <Box>
                    <Typography
                      variant={msg?.assetList?.length ? 'subtitle1' : 'body1'}
                      fontSize={14}
                    >
                      {msg?.assetList?.length
                        ? msg.content
                        : 'I couldn’t find any assets for you. Let me know if you’d like to try something else!'}
                    </Typography>
                    {msg?.assetList?.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {msg.assetList.map((item, index) => (
                          <Button
                            key={index}
                            variant="outlined"
                            onClick={() => handleAssetClick(msg, item)}
                          >
                            {item?.name}
                          </Button>
                        ))}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <>
                    <Typography
                      fontSize={14}
                      sx={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{
                        __html: msg.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'),
                      }}
                    />
                    {selectedAsset?.assetId &&
                      msg?.isSubmitTicket &&
                      (formData?.ticketTitle || formData?.description) && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            mt: 2,
                            p: 2,
                            gap: 1,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            minWidth: 300,
                          }}
                        >
                          <TextField
                            value={formData?.ticketTitle}
                            label="Ticket Title"
                            fullWidth
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                ticketTitle: e.target.value,
                              }))
                            }
                            multiline
                            maxRows={3}
                          />

                          <TextField
                            value={formData?.description}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                description: e.target.value,
                              }))
                            }
                            label="Description"
                            multiline
                            maxRows={10}
                            fullWidth
                          />
                          <Button
                            onClick={() => handleSubmit(formData, selectedAsset?.assetId, msg)}
                          >
                            Submit
                          </Button>
                        </Box>
                      )}
                  </>
                )}
                {msg?.raiseTicket && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1 }}>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() => handleRaiseTicket(msg?.id, 'yes')}
                    >
                      Yes
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() => handleRaiseTicket(msg?.id, 'no')}
                    >
                      No
                    </Button>
                  </Box>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" ml={1}>
                {formatTime(msg.timestamp)}
              </Typography>
            </Box>
          </Box>
        ))}

        {/* Typing */}
        {isTyping && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 28, height: 28, fontSize: 12 }}>
              AI
            </Avatar>
            <Box
              sx={{
                ml: 1,
                width: 32,
                height: 10,
                borderRadius: 2,
                background: '#e0e0e0',
                animation: 'typingBlink 1s infinite',
                '@keyframes typingBlink': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.3 },
                  '100%': { opacity: 1 },
                },
              }}
            />
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box sx={{ p: 2, pb: 1, borderTop: '1px solid #ddd' }}>
        <TextField
          fullWidth
          placeholder="Type a message..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend(inputValue);
            }
          }}
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => handleSend(inputValue)} sx={{ color: 'primary.dark' }}>
                  <Iconify icon="ic:round-send" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box
          sx={{
            typography: 'caption',
            textAlign: 'center',
            color: 'grey.400',
            fontStyle: 'italic',
            fontSize: 10,
          }}
        >
          AI can make mistakes. Check important info.
        </Box>
      </Box>
    </Paper>
  );
}

function normalizeMac(mac) {
  return (
    mac
      .replace(/[^a-fA-F0-9]/g, '') // remove colons, dashes, etc.
      .toLowerCase()
      .match(/.{1,2}/g) // split every 2 chars
      ?.join(':') || mac
  );
}
